// Auth utility functions

/**
 * Get stored auth token from localStorage
 * @returns {string|null} The stored token or null
 */
export const getStoredToken = () => {
  try {
    return localStorage.getItem('auth_token');
  } catch (error) {
    console.error('Error getting stored token:', error);
    return null;
  }
};

/**
 * Get stored user data from localStorage
 * @returns {object|null} The stored user data or null
 */
export const getStoredUser = () => {
  try {
    const userData = localStorage.getItem('auth_user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting stored user:', error);
    return null;
  }
};

/**
 * Check if user is authenticated based on stored data
 * @returns {boolean} True if authenticated, false otherwise
 */
export const isAuthenticated = () => {
  const token = getStoredToken();
  const user = getStoredUser();
  return !!(token && user);
};

/**
 * Clear all auth data from localStorage
 */
export const clearAuthData = () => {
  try {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

/**
 * Check if token is expired (basic check)
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // Decode JWT payload (without verification - just for expiration check)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    
    // Check if token is expired (with 5 minute buffer)
    return payload.exp < (currentTime + 300);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Get token expiration time
 * @param {string} token - JWT token
 * @returns {Date|null} Expiration date or null if invalid
 */
export const getTokenExpiration = (token) => {
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return new Date(payload.exp * 1000);
  } catch (error) {
    console.error('Error getting token expiration:', error);
    return null;
  }
};

/**
 * Format error message for display
 * @param {Error|string} error - Error object or message
 * @returns {string} Formatted error message
 */
export const formatAuthError = (error) => {
  if (typeof error === 'string') return error;
  
  if (error.response) {
    // API error response
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return data.detail || 'Invalid credentials';
      case 401:
        return 'Unauthorized access';
      case 403:
        return 'Access forbidden';
      case 404:
        return 'Service not found';
      case 500:
        return 'Server error occurred';
      default:
        return data.detail || 'An error occurred';
    }
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} Validation result with isValid and message
 */
export const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, message: 'Password is required' };
  }
  
  if (password.length < 3) {
    return { isValid: false, message: 'Password must be at least 3 characters long' };
  }
  
  if (password.length > 128) {
    return { isValid: false, message: 'Password must be less than 128 characters' };
  }
  
  // API doesn't enforce complex password requirements
  // Just check for basic length and non-empty
  return { isValid: true, message: 'Password is valid' };
};

/**
 * Validate username
 * @param {string} username - Username to validate
 * @returns {object} Validation result with isValid and message
 */
export const validateUsername = (username) => {
  if (!username) {
    return { isValid: false, message: 'Username is required' };
  }
  
  if (username.length < 3) {
    return { isValid: false, message: 'Username must be at least 3 characters long' };
  }
  
  if (username.length > 50) {
    return { isValid: false, message: 'Username must be less than 50 characters' };
  }
  
  // Check for valid characters (alphanumeric, underscore, hyphen)
  const validChars = /^[a-zA-Z0-9_-]+$/;
  if (!validChars.test(username)) {
    return { 
      isValid: false, 
      message: 'Username can only contain letters, numbers, underscores, and hyphens' 
    };
  }
  
  return { isValid: true, message: 'Username is valid' };
};

/**
 * Create authorization header
 * @param {string} token - Auth token
 * @returns {object} Authorization header object
 */
export const createAuthHeader = (token) => {
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Check if current user has specific permission
 * @param {object} user - User object
 * @param {string} permission - Permission to check
 * @returns {boolean} True if user has permission
 */
export const hasPermission = (user, permission) => {
  // For now, all authenticated users have all permissions
  // This can be extended later with role-based permissions
  return !!user;
};

/**
 * Get user display name
 * @param {object} user - User object
 * @returns {string} Display name or fallback
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'Unknown User';
  
  return user.displayName || user.name || 'Unknown User';
};

/**
 * Get user initials for avatar
 * @param {object} user - User object
 * @returns {string} User initials
 */
export const getUserInitials = (user) => {
  if (!user) return 'U';
  
  const displayName = user.displayName || user.name || '';
  const names = displayName.split(' ');
  
  if (names.length >= 2) {
    return (names[0][0] + names[1][0]).toUpperCase();
  }
  
  return displayName.charAt(0).toUpperCase() || 'U';
};
